import React, { useState, useEffect, useMemo } from 'react';
import { Button, FormGroup, Input, Label, Row } from 'reactstrap';
import moment from 'moment';
import DataTable from 'react-data-table-component';
import { useSelector } from 'react-redux';
import BreadCrumbs from '../../layouts/breadcrumbs/BreadCrumbs';
import ComponentCard from '../ComponentCard';
import useHttp from '../../hooks/useHttp';
import useAlert from '../../hooks/useAlert';
import Loader from '../../layouts/loader/Loader';
import FilterComponent from '../utils/Filter';
import customStyles from '../TableStyles';

const OrphanInvoices = () => {
  const [fromDate, setFromDate] = useState(moment().subtract(7, 'days').format('YYYY-MM-DD'));
  const [toDate, setToDate] = useState(moment().format('YYYY-MM-DD'));
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoiceRows, setSelectedInvoiceRows] = useState([]);
  const [filterText, setFilterText] = useState('');
  const isDarkMode = useSelector((state) => state.customizer.isDark);
  const { isLoading, error, sendRequest } = useHttp();
  const { alert, successAlert, errorAlert } = useAlert();

  useEffect(() => {
    if (error) errorAlert(error);
  }, [error]);

  const fetchInvoices = () => {
    if (!fromDate || !toDate) {
      errorAlert('Please select both From and To dates.');
      return;
    }

    sendRequest(
      {
        method: 'GET',
        url: `${process.env.REACT_APP_IBILL_GATEWAY}/invoice/api/v1/sync/orphan-invoice/list?fromDate=${fromDate}&toDate=${toDate}`,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      },
      (data) => {
        setInvoices(data);
      }
    );
  };

  const deleteSelectedInvoices = () => {
    const selectedIds = selectedInvoiceRows.map((row) => row.id);

    if (selectedIds.length === 0) {
      errorAlert('No invoices selected for deletion.');
      return;
    }

    /*const confirmDelete = window.confirm('Are you sure you want to delete the selected invoices?');
    if (!confirmDelete) {
      return;
    }*/

    sendRequest(
      {
        method: 'DELETE',
        url: `${process.env.REACT_APP_IBILL_GATEWAY}/invoice/api/v1/sync/invoice/delete`,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: { invoiceIds: selectedIds },
      },
      () => {
        successAlert('Selected invoices deleted successfully.');
        setInvoices((prev) => prev.filter((invoice) => !selectedIds.includes(invoice.id)));
        setSelectedInvoiceRows([]);
      }
    );
  };

  const columns = [
    {
      name: 'Invoice Date',
      selector: (row) => moment(row.invoiceDate).format('DD-MM-YYYY'),
      sortable: true,
      reorder: true,
      width: '150px'
    },
    { name: 'Customer ID', selector: (row) => row.customerId, sortable: true , width: '150px'},
    { name: 'Customer Name', selector: (row) => row.customerName, sortable: true, width: '200px', wrap: true },
    { name: 'Zone Name', selector: (row) => row.zoneName, sortable: true, width: '200px', wrap: true },
    { name: 'Pack Name', selector: (row) => row.packName, sortable: true , width: '150px'},
    { name: 'Amount', selector: (row) => row.amount, sortable: true , width: '150px'},
    { name: 'Order ID', selector: (row) => row.orderIdString, sortable: true, width: '200px' },
    { name: 'Invoice Number', selector: (row) => row.invoiceNumber, sortable: true, width: '200px' },
    {
      name: 'Cancelled',
      selector: (row) => (row.isCancelledInvoice ? 'Yes' : 'No'),
      sortable: true,
      width: '120px'
    },
  ];

  const filteredInvoices = invoices.filter(
    (invoice) =>
      invoice.orderIdString?.toLowerCase().includes(filterText.toLowerCase()) ||
      invoice.customerName?.toLowerCase().includes(filterText.toLowerCase())
  );

  const subHeaderComponent = useMemo(
    () => <FilterComponent onFilter={(e) => setFilterText(e.target.value)} filterText={filterText} />,
    [filterText]
  );

  return (
    <div>
      {alert}
      <BreadCrumbs />
      <ComponentCard title="Orphan Invoices">
        <Row>
          <FormGroup className="col-sm-3 col-xs-12">
            <Label>From Date</Label>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </FormGroup>
          <FormGroup className="col-sm-3 col-xs-12">
            <Label>To Date</Label>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </FormGroup>
          <FormGroup className="col-sm-3 col-xs-12">
            <Label>&nbsp;</Label>
            <Button className="btn col-12 btn-success" onClick={fetchInvoices}>
              Search
            </Button>
          </FormGroup>
          <FormGroup className="col-sm-3 col-xs-12">
            <Label>&nbsp;</Label>
            <Button className="btn col-12 btn-danger" onClick={deleteSelectedInvoices}>
              Delete All
            </Button>
          </FormGroup>
        </Row>
        {isLoading ? (
          <Loader />
        ) : (
          <DataTable
            columns={columns}
            data={filteredInvoices}
            selectableRows
            onSelectedRowsChange={({ selectedRows }) => setSelectedInvoiceRows(selectedRows)}
            theme={isDarkMode ? 'iDark' : 'iLight'}
            customStyles={customStyles}
            subHeader
            subHeaderComponent={subHeaderComponent}
            pagination
          />
        )}
      </ComponentCard>
    </div>
  );
};

export default OrphanInvoices;
